<template>
  <div v-if="modelValue" class="advanced-login-modal">
    <div class="advanced-login-modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          Connexion avancée via token
        </h5>
        <button type="button" class="btn-close" @click="closeModal" />
      </div>
      <div class="modal-body">
        <p>
          Pour certains comptes, la connexion standard peut ne pas fonctionner. Vous pouvez alors utiliser cette méthode alternative :
        </p>
        <p class="alert alert-info">
          <strong>Note :</strong> Vous pouvez récupérer le token sur un PC puis l'utiliser sur mobile ou n'importe quel autre appareil.
        </p>
        <ol>
          <li>Rendez-vous sur <a href="https://mpg.football" target="_blank">mpg.football</a> et connectez-vous à votre compte</li>
          <li>Une fois connecté, ouvrez les outils de développement en appuyant sur <kbd>F12</kbd> (ou clic droit > Inspecter)</li>
          <li>Cliquez sur l'onglet <strong>Réseau</strong> (ou <strong>Network</strong>)</li>
          <li>Dans la barre de filtre en haut, tapez <code>user</code> pour filtrer les requêtes</li>
          <li>Si aucune requête n'apparait, rechargez la page ou naviguez sur le site pour déclencher des appels API</li>
          <li>Cherchez et cliquez sur une requête vers <code>https://api.mpg.football/user</code></li>
          <li>Dans le panneau de détails qui s'ouvre, cliquez sur l'onglet <strong>Headers</strong></li>
          <li>Faites défiler jusqu'à la section <strong>Request Headers</strong></li>
          <li>Trouvez la ligne <code>Authorization</code> et copiez sa valeur complète</li>
          <li>Collez ce token ci-dessous :</li>
        </ol>
        <div class="form-floating mb-3">
          <input
            id="mpg-token"
            v-model="tokenInput"
            type="text"
            class="form-control"
            placeholder="token MPG"
          >
          <label for="mpg-token" class="form-label">Token MPG</label>
        </div>
        <div v-if="errorMessage" class="alert alert-danger mb-3">
          {{ errorMessage }}
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeModal">
          Annuler
        </button>
        <button class="btn btn-success" :disabled="!tokenInput" @click="signIn">
          Connexion avec token
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useMPG } from "@/use/useMPG";
import { ref } from "vue";

defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["update:modelValue"]);

const { signInWithToken } = useMPG();
const tokenInput = ref("");

const closeModal = () => {
  emit("update:modelValue", false);
};

const errorMessage = ref("");

const validateToken = (token) => {
  if (! token || token.trim() === "") {
    return {
      valid: false,
      message: "Le token ne peut pas être vide.",
    };
  }
  if (token.length < 10) {
    return {
      valid: false,
      message: "Le token est trop court. Veuillez vérifier votre saisie.",
    };
  }
  return {
    valid: true,
    message: "",
  };
};

const signIn = () => {
  errorMessage.value = "";
  if (tokenInput.value) {
    const validationResult = validateToken(tokenInput.value);
    if (! validationResult.valid) {
      errorMessage.value = validationResult.message;
      return;
    }
    signInWithToken(tokenInput.value);
    closeModal();
  } else {
    errorMessage.value = "Veuillez saisir un token.";
  }
};
</script>

<style lang="scss" scoped>
.advanced-login-modal {
  position: fixed;
  z-index: 1000;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.advanced-login-modal-content {
  background-color: #fff;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);

  .modal-header {
    padding: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #dee2e6;
  }
  .modal-body {
    padding: 1rem;
  }
  .modal-footer {
    padding: 1rem;
    border-top: 1px solid #dee2e6;
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  .token-code {
    background-color: #f8f9fa;
    padding: 0.75rem;
    border-radius: 4px;
    font-family: monospace;
    overflow-x: auto;
    margin: 0.5rem 0;
  }

  .token-grabber-img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    max-height: 250px;
  }
}
</style>